declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

/** SHA-256 hash a string using the Web Crypto API. Returns hex string. */
export const hashValue = async (value: string): Promise<string> => {
  const data = new TextEncoder().encode(value);
  const buffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/** Decode the `sub` claim from a JWT without verifying the signature. */
export const decodeJwtSub = (token: string): string | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.sub ?? null;
  } catch {
    return null;
  }
};

export type AnalyticsParams = Record<string, unknown>;

export const pushEvent = (eventName: string, params: AnalyticsParams = {}): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
};

export const setUserProperties = (properties: AnalyticsParams): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('set', 'user_properties', properties);
};

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const UTM_STORAGE_KEY = 'teyaseer_utm';

/** Persist UTM params from the current URL into sessionStorage for use post-redirect. */
export const captureUtmParams = (): void => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utm: AnalyticsParams = {};
  UTM_KEYS.forEach(key => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
  }
};

/** Read previously captured UTM params. Returns empty object if none stored. */
export const getStoredUtmParams = (): AnalyticsParams => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const FIRST_LOGIN_KEY = 'teyaseer_has_logged_in';

/** Returns true only on the very first login for this browser. Marks the flag after reading. */
export const checkAndMarkFirstLogin = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hasLoggedIn = localStorage.getItem(FIRST_LOGIN_KEY);
  if (!hasLoggedIn) {
    localStorage.setItem(FIRST_LOGIN_KEY, '1');
    return true;
  }
  return false;
};

const SESSION_ID_KEY = 'teyaseer_session_id';

/** Returns a stable session ID for the current browser session, creating one if needed. */
export const getOrCreateSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
};

const LAST_LOGIN_KEY = 'teyaseer_last_login_ts';
const REACTIVATION_THRESHOLD_DAYS = 45;

/**
 * Returns days since the last stored login timestamp.
 * Returns null if no prior login is recorded.
 */
export const getDaysSinceLastLogin = (): number | null => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(LAST_LOGIN_KEY);
  if (!raw) return null;
  const diffMs = Date.now() - parseInt(raw, 10);
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/** Persist the current login timestamp. Call after a successful login. */
export const recordLoginTimestamp = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LAST_LOGIN_KEY, String(Date.now()));
};

/**
 * Returns true when the user has been inactive for REACTIVATION_THRESHOLD_DAYS+ days.
 */
export const isReactivatedUser = (): boolean => {
  const days = getDaysSinceLastLogin();
  return days !== null && days >= REACTIVATION_THRESHOLD_DAYS;
};
